// Derivation helpers for the soft-completion explain sections.
// Formulas follow explains-page-contract.json ("derived" sources); the
// symbol -> asset-class join is recovered client-side from the
// allocation trees + residual security-mapping rows carried in the
// payload (contract `joins.symbol_to_ac` notes the server-side map is
// authoritative — this is the documented partial recovery).
import { parseNumeric } from "../../../../lib/numbers";
import type {
  AccountRecord,
  CompletionData,
  SoftCompletionData,
} from "../../lib/explainTypes";

export const num = parseNumeric;

// The engine's cash pseudo-symbol and the label its allocation-tree
// bucket carries.
export const CASH_SYMBOL = "CASH-TRADE";
export const UNKNOWN_AC = "unknown";

// Recover the symbol -> asset-class map from every account's
// allocation tree (risk -> asset class -> symbol leaves) plus the
// residual security-mapping rows. Symbols the payload never classifies
// resolve to `unknown`.
export function buildSymbolToAssetClass(
  accounts: AccountRecord[],
  completion: CompletionData,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const account of accounts) {
    for (const riskNode of account.constrained_allocation?.allocation_tree ??
      []) {
      for (const acNode of riskNode.children ?? []) {
        for (const leaf of acNode.children ?? []) {
          if (map[leaf.name] == null) map[leaf.name] = acNode.name;
        }
      }
    }
  }
  for (const model of Object.values(completion.residual_models ?? {})) {
    for (const row of model.residual_calculation?.security_mapping_rows ?? []) {
      if (map[row.symbol] == null) map[row.symbol] = row.asset_class;
    }
  }
  return map;
}

export function assetClassOf(
  symbol: string,
  symbolToAc: Record<string, string>,
): string {
  if (symbol === CASH_SYMBOL) return UNKNOWN_AC;
  const ac = symbolToAc[symbol];
  if (ac == null || ac === "Unknown") return UNKNOWN_AC;
  return ac;
}

// Group a symbol -> weight/dollar map by asset class.
export function groupByAssetClass(
  bySymbol: Record<string, string> | null | undefined,
  symbolToAc: Record<string, string>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [symbol, value] of Object.entries(bySymbol ?? {})) {
    const ac = assetClassOf(symbol, symbolToAc);
    out[ac] = (out[ac] ?? 0) + num(value);
  }
  return out;
}

// Sum of absolute per-asset-class differences between two AC-weight
// maps, in percentage points. The contract's deviation metric
// (`deviation(A,B) = sum|A_ac - B_ac| in pp`); the cash/unknown bucket
// is excluded so all-cash starting states read as distance on the
// invested classes.
export function mixDeviationPp(
  a: Record<string, number>,
  b: Record<string, number>,
): number {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  keys.delete(UNKNOWN_AC);
  let total = 0;
  for (const key of keys) total += Math.abs((a[key] ?? 0) - (b[key] ?? 0));
  return total * 100;
}

export function formatPp(v: number, precision = 0): string {
  return `${v.toFixed(precision)}pp`;
}

export function formatSignedPp(v: number, precision = 1): string {
  return `${v > 0 ? "+" : ""}${v.toFixed(precision)}pp`;
}

// Entries of a name -> Decimal-string map, sorted by value descending.
export function sortedByValueDesc(
  map: Record<string, string> | null | undefined,
): [string, number][] {
  return Object.entries(map ?? {})
    .map(([k, v]) => [k, num(v)] as [string, number])
    .sort((a, b) => b[1] - a[1]);
}

// Own-model accounts (role "model"), in processing order.
export function ownModelAccountIds(completion: CompletionData): number[] {
  return (completion.account_classifications ?? [])
    .filter((c) => c.processing_group === "model")
    .sort((a, b) => a.processing_order - b.processing_order)
    .map((c) => c.account_id);
}

// No-model (residual) accounts, in processing order.
export function residualAccountIds(completion: CompletionData): number[] {
  return (completion.account_classifications ?? [])
    .filter((c) => c.processing_group !== "model")
    .sort((a, b) => a.processing_order - b.processing_order)
    .map((c) => c.account_id);
}

export function accountById(
  accounts: AccountRecord[],
): Map<number, AccountRecord> {
  return new Map(accounts.map((a) => [a.account_id, a]));
}

// Asset classes present in a model's primary weights (weight > 0).
export function modelAssetClassSpan(
  primary: Record<string, string> | null | undefined,
  symbolToAc: Record<string, string>,
): Set<string> {
  const span = new Set<string>();
  for (const [symbol, weight] of Object.entries(primary ?? {})) {
    if (num(weight) > 0) span.add(assetClassOf(symbol, symbolToAc));
  }
  span.delete(UNKNOWN_AC);
  return span;
}

// E1 with client-side fallback: asset classes in the household budget
// that an own-model account's model cannot reach. Prefers the payload
// enrichment (contract E1) over the set difference.
export function unreachableAcsForAccount(
  accountId: number,
  completion: CompletionData,
  accounts: Map<number, AccountRecord>,
  symbolToAc: Record<string, string>,
): string[] {
  const detail = completion.account_details?.[String(accountId)];
  if (detail?.unreachable_household_acs != null) {
    return detail.unreachable_household_acs;
  }
  const budgetAcs = Object.keys(
    detail?.household_target_by_asset_class ??
      completion.household_target_by_asset_class ??
      {},
  );
  const span = modelAssetClassSpan(
    accounts.get(accountId)?.model?.primary,
    symbolToAc,
  );
  return budgetAcs.filter((ac) => !span.has(ac));
}

// E1 with client-side fallback: household-model symbols carried by no
// account model.
export function householdOnlySymbols(
  completion: SoftCompletionData,
  accounts: AccountRecord[],
  householdPrimary: Record<string, string> | null | undefined,
): string[] {
  if (completion.household_only_symbols != null) {
    return completion.household_only_symbols;
  }
  const ownModelIds = new Set(ownModelAccountIds(completion));
  const accountSymbols = new Set<string>();
  for (const account of accounts) {
    if (!ownModelIds.has(account.account_id)) continue;
    for (const symbol of Object.keys(account.model?.primary ?? {})) {
      accountSymbols.add(symbol);
    }
  }
  return Object.keys(householdPrimary ?? {}).filter(
    (s) => !accountSymbols.has(s),
  );
}

// True when any classification row carries the E2 enrichment.
export function hasGainsAtRiskEnrichment(completion: CompletionData): boolean {
  return (completion.account_classifications ?? []).some(
    (c) =>
      c.gains_at_risk_dollars != null ||
      c.held_dollars != null ||
      c.model_target_dollars != null ||
      c.contested_ac != null,
  );
}

// True when any account detail carries the E3 soft-cap enrichment.
export function hasSoftCapEnrichment(completion: CompletionData): boolean {
  return Object.values(completion.account_details ?? {}).some(
    (d) => d.capped_to_pct != null || d.after_cap_model != null,
  );
}
